export type SeriesDatum = {
  id: number | string;
  name: string;
  value: number;
};

type ColorMapItem = {
  id: number | string;
};

// Variants only exist to survive more series than base colors. The floor keeps every
// tier legible against the panel surface — below ~55% a slice reads as background.
const COLOR_VARIANT_STRENGTHS = [86, 72, 58];

const hashIdentity = (identity: number | string) => {
  const value = `${typeof identity}:${String(identity)}`;
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

const buildExtendedPalette = (palette: string[]) => {
  if (palette.length === 0) return [];

  // Saturated tokens first, then one full tier per strength. Probing walks forward, so
  // this layout hands out every base color before it ever reaches a diluted one.
  return [
    ...palette,
    ...COLOR_VARIANT_STRENGTHS.flatMap((strength) =>
      palette.map(
        (color) =>
          `color-mix(in srgb, ${color} ${strength}%, var(--app-surface))`,
      ),
    ),
  ];
};

export const buildColorMap = <T extends ColorMapItem>(
  items: T[],
  palette: string[],
) => {
  const extendedPalette = buildExtendedPalette(palette);
  const colorMap = new Map<number | string, string>();
  const usedColors = new Set<string>();

  [...items]
    .sort((first, second) =>
      `${typeof first.id}:${String(first.id)}`.localeCompare(
        `${typeof second.id}:${String(second.id)}`,
      ),
    )
    .forEach((item) => {
      if (colorMap.has(item.id) || extendedPalette.length === 0) return;

      // Hash over the base palette only, so the first choice is always a saturated token.
      const preferredIndex = hashIdentity(item.id) % palette.length;
      // Exhaust every base color (wrapping inside the base range) before touching a
      // diluted tier — otherwise a collision skips free saturated slots.
      const probeOrder = [
        ...palette.map((_, offset) => (preferredIndex + offset) % palette.length),
        ...extendedPalette
          .slice(palette.length)
          .map((_, offset) => palette.length + offset),
      ];
      const color = probeOrder
        .map((index) => extendedPalette[index])
        .find((candidate) => !usedColors.has(candidate));

      if (!color) return;

      colorMap.set(item.id, color);
      usedColors.add(color);
    });

  return colorMap;
};

export const toShareSegments = (
  items: SeriesDatum[],
  colorById: Map<number | string, string>,
  fallbackColors: string[],
) => {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  if (total <= 0) return [];

  return [...items]
    .sort((first, second) => second.value - first.value)
    .map((item, index) => ({
      id: item.id,
      name: item.name,
      color: colorById.get(item.id) ?? fallbackColors[index % fallbackColors.length],
      percent: (item.value / total) * 100,
    }));
};
